import React, { useEffect, useState, useRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaTasks, FaCalendarAlt, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { fetchTicketsByQueueQuarterly } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

// Calendar component for selecting date range within last 90 days
const CalendarPicker = ({ onDateSelect, onClose }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const [selectedDayRange, setSelectedDayRange] = useState(90); // Default to 90 days
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);

  // Calculate minDate based on selected day range
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - selectedDayRange);

  const dayRangeOptions = [30, 60, 90];
  const currentIndex = dayRangeOptions.indexOf(selectedDayRange);

  const handlePrevRange = () => {
    if (currentIndex > 0) {
      setSelectedDayRange(dayRangeOptions[currentIndex - 1]);
      setSelectedStart(null);
      setSelectedEnd(null);
    }
  };

  const handleNextRange = () => {
    if (currentIndex < dayRangeOptions.length - 1) {
      setSelectedDayRange(dayRangeOptions[currentIndex + 1]);
      setSelectedStart(null);
      setSelectedEnd(null);
    }
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dayNames = ["M", "T", "W", "T", "F", "S", "S"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonth.getDate() - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonth.getDate() - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 35 - days.length;
    for (let i = 1; i <= Math.max(0, remaining); i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days.slice(0, 35);
  };

  const days = getDaysInMonth(currentMonth);

  const isDateInRange = (date) => {
    return date >= minDate && date <= today;
  };

  const isSelected = (date) => {
    if (!selectedStart && !selectedEnd) return false;
    if (selectedStart && date.getTime() === selectedStart.getTime()) return true;
    if (selectedEnd && date.getTime() === selectedEnd.getTime()) return true;
    if (selectedStart && selectedEnd) {
      return date >= selectedStart && date <= selectedEnd;
    }
    return false;
  };

  const isInRange = (date) => {
    if (!selectedStart || !selectedEnd) return false;
    return date > selectedStart && date < selectedEnd;
  };

  const handleDateClick = (date) => {
    if (!isDateInRange(date)) return;

    if (selectingStart || (!selectedStart && !selectedEnd)) {
      setSelectedStart(date);
      setSelectedEnd(null);
      setSelectingStart(false);
    } else {
      if (date < selectedStart) {
        setSelectedStart(date);
        setSelectedEnd(selectedStart);
      } else {
        setSelectedEnd(date);
      }
    }
  };

  const handleApply = () => {
    if (selectedStart && selectedEnd) {
      onDateSelect(selectedStart, selectedEnd);
    } else if (selectedStart) {
      onDateSelect(selectedStart, selectedStart);
    }
  };

  const isDarkMode = document.body.classList.contains('dark-mode');

  // Calculate month navigation restrictions
  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
  const prevMonthEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
  const canGoPrev = prevMonthEnd >= minDate;

  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
  const nextMonthStart = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
  const canGoNext = nextMonthStart <= today;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -5, scale: 0.98 }}
      transition={{ duration: 0.12 }}
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        zIndex: 1000,
        background: isDarkMode ? "#1e2139" : "#fff",
        borderRadius: 12,
        boxShadow: isDarkMode 
          ? "0 8px 30px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)" 
          : "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
        padding: "10px",
        width: 240,
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ 
        fontSize: 11, 
        fontWeight: 600, 
        color: isDarkMode ? "#a0a4b8" : "#6b7280", 
        marginBottom: 8,
        paddingBottom: 6,
        borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6"
      }}>
        Select Date Range (Last {selectedDayRange} Days)
      </div>

      {/* Day Range Badge with Arrows */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        gap: 8, 
        marginBottom: 10,
        padding: "6px",
        background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        borderRadius: 8
      }}>
        <button
          onClick={handlePrevRange}
          disabled={currentIndex === 0}
          style={{
            background: "none",
            border: "none",
            cursor: currentIndex === 0 ? "not-allowed" : "pointer",
            padding: 4,
            borderRadius: 4,
            color: currentIndex === 0 ? (isDarkMode ? "#4a4f63" : "#d1d5db") : (isDarkMode ? "#a0a4b8" : "#6b7280"),
            display: "flex",
            alignItems: "center",
            opacity: currentIndex === 0 ? 0.3 : 1,
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (currentIndex > 0) {
              e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          <FaChevronLeft size={10} />
        </button>
        
        <div style={{
          padding: "6px 12px",
          borderRadius: 6,
          background: "rgba(139, 92, 246, 0.15)",
          color: "#8b5cf6",
          fontSize: 11,
          fontWeight: 600,
          minWidth: 80,
          textAlign: "center",
        }}>
          Last {selectedDayRange} Days
        </div>

        <button
          onClick={handleNextRange}
          disabled={currentIndex === dayRangeOptions.length - 1}
          style={{
            background: "none",
            border: "none",
            cursor: currentIndex === dayRangeOptions.length - 1 ? "not-allowed" : "pointer",
            padding: 4,
            borderRadius: 4,
            color: currentIndex === dayRangeOptions.length - 1 ? (isDarkMode ? "#4a4f63" : "#d1d5db") : (isDarkMode ? "#a0a4b8" : "#6b7280"),
            display: "flex",
            alignItems: "center",
            opacity: currentIndex === dayRangeOptions.length - 1 ? 0.3 : 1,
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (currentIndex < dayRangeOptions.length - 1) {
              e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          <FaChevronRight size={10} />
        </button>
      </div>

      {/* Calendar Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <button
          onClick={() => canGoPrev && setCurrentMonth(prevMonth)}
          disabled={!canGoPrev}
          style={{ 
            background: "none", 
            border: "none", 
            cursor: canGoPrev ? "pointer" : "not-allowed", 
            padding: 3, 
            borderRadius: 4, 
            color: canGoPrev ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), 
            display: "flex", 
            alignItems: "center",
            opacity: canGoPrev ? 1 : 0.3
          }}
          onMouseEnter={(e) => {
            if (canGoPrev) {
              e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          <FaChevronLeft size={9} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 11, color: isDarkMode ? "#e2e4e9" : "#1f2937" }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          onClick={() => canGoNext && setCurrentMonth(nextMonth)}
          disabled={!canGoNext}
          style={{ 
            background: "none", 
            border: "none", 
            cursor: canGoNext ? "pointer" : "not-allowed", 
            padding: 3, 
            borderRadius: 4, 
            color: canGoNext ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), 
            display: "flex", 
            alignItems: "center",
            opacity: canGoNext ? 1 : 0.3
          }}
          onMouseEnter={(e) => {
            if (canGoNext) {
              e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          <FaChevronRight size={9} />
        </button>
      </div>

      {/* Day names */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 3 }}>
        {dayNames.map((day, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 8, fontWeight: 600, color: isDarkMode ? "#6b7280" : "#9ca3af", padding: "1px 0" }}>
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {days.map((dayObj, i) => {
          const dateInRange = isDateInRange(dayObj.date);
          const selected = isSelected(dayObj.date);
          const inRange = isInRange(dayObj.date);
          const disabled = !dayObj.isCurrentMonth || !dateInRange;

          return (
            <button
              key={i}
              onClick={() => handleDateClick(dayObj.date)}
              disabled={disabled}
              style={{
                width: 26,
                height: 26,
                borderRadius: 5,
                border: selected ? "2px solid #8b5cf6" : "1px solid transparent",
                background: selected 
                  ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" 
                  : inRange
                    ? (isDarkMode ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.1)")
                    : "transparent",
                color: selected ? "#fff" : disabled ? (isDarkMode ? "#4a4f63" : "#d1d5db") : (isDarkMode ? "#e2e4e9" : "#374151"),
                fontSize: 10,
                fontWeight: selected ? 600 : 400,
                cursor: disabled ? "default" : "pointer",
                transition: "all 0.12s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                padding: 0,
                margin: 0,
                lineHeight: 1,
                textAlign: "center",
              }}
              onMouseEnter={(e) => {
                if (!disabled && !selected) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !selected) {
                  e.currentTarget.style.background = inRange 
                    ? (isDarkMode ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.1)")
                    : "transparent";
                }
              }}
            >
              {dayObj.day}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        gap: 6, 
        marginTop: 10, 
        paddingTop: 10, 
        borderTop: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6" 
      }}>
        <button
          onClick={onClose}
          style={{
            padding: "5px 10px",
            borderRadius: 5,
            border: "none",
            background: "transparent",
            color: isDarkMode ? "#a0a4b8" : "#6b7280",
            fontSize: 10,
            fontWeight: 500,
            cursor: "pointer",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? "rgba(255,255,255,0.1)" : "#f3f4f6"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          disabled={!selectedStart || !selectedEnd}
          style={{
            padding: "5px 10px",
            borderRadius: 5,
            border: "none",
            background: (selectedStart && selectedEnd) ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : (isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"),
            color: (selectedStart && selectedEnd) ? "#fff" : (isDarkMode ? "#6b7280" : "#9ca3af"),
            fontSize: 10,
            fontWeight: 600,
            cursor: (selectedStart && selectedEnd) ? "pointer" : "not-allowed",
            opacity: (selectedStart && selectedEnd) ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (selectedStart && selectedEnd) {
              e.currentTarget.style.opacity = "0.9";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedStart && selectedEnd) {
              e.currentTarget.style.opacity = "1";
            }
          }}
        >
          Apply
        </button>
      </div>
    </motion.div>
  );
};

const DateRangePicker = ({ startDate, endDate, onRangeSelect, onClose }) => {
  return (
    <CalendarPicker
      onDateSelect={onRangeSelect}
      onClose={onClose}
    />
  );
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const colors = ["#6f42c1", "#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#6366f1", "#ec4899", "#14b8a6", "#f97316", "#8b5cf6"];

// Must match the label set in spog_dashboard/utils.py's apply_helpdesk_bar.
const HELP_DESK_LABEL = "Help Desk";

const TicketQueueAreaChart = ({ onFilterApplied, clientId, onHelpdeskBarClick }) => {
  const { getCachedData, setCachedData } = useDataCache();

  // Date range state - default to last 90 days
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return { 
      api: { start, end },      // Dates sent to API
      display: { start, end }   // Dates shown in UI
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    queues: [],
    ticket_counts: [],
    avg_ages: [],
    date_range: { start_date: null, end_date: null },
    total_tickets: 0
  });

  const datePickerRef = useRef(null);
  const isMobile = window.innerWidth <= 992;

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString('en-US', options);
  };

  // Get computed text color for chart
  const getAxisTextColor = () => {
    const isDark = document.body.classList.contains('dark-mode');
    return isDark ? '#a0a4b8' : '#6c737f';
  };

  // Handle date range selection (API and display are the same now)
  const handleDateRangeSelect = (start, end) => {
    setDateRange({ 
      api: { start, end },
      display: { start, end }
    });
    setShowDatePicker(false);
  };

  // Clear date range (reset to default 90 days)
  const clearDateRange = (e) => {
    e.stopPropagation();
    setDateRange(getDefaultDateRange());
  };

  // Get subtitle based on display date range
  const getSubtitle = () => {
    const startStr = formatDateForDisplay(dateRange.display.start);
    const endStr = formatDateForDisplay(dateRange.display.end);
    return `Showing: ${startStr} - ${endStr}`;
  };

  // Fetch data when client or date range changes (use cache so tab switch does not reload)
  useEffect(() => {
    if (!clientId) return;

    const startDay = new Date(dateRange.api.start);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(dateRange.api.end);
    endDay.setHours(0, 0, 0, 0);
    const cacheKey = `tickets_by_queue_${startDay.getTime()}_${endDay.getTime()}_all`;
    const cached = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      setSummaryData(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchTicketsByQueueQuarterly(clientId, dateRange.api.start, dateRange.api.end)
      .then((res) => {
        setSummaryData(res);
        setCachedData(cacheKey, res, clientId);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch queue summary:", error);
        setSummaryData({
          queues: [],
          ticket_counts: [],
          avg_ages: [],
          date_range: { start_date: null, end_date: null },
          total_tickets: 0
        });
        setLoading(false);
      });
  }, [clientId, dateRange, getCachedData, setCachedData]);

  // Handle click outside date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  // Build chart data from API response
  const chartData = useMemo(() => {
    const { queues, ticket_counts } = summaryData;

    // X-axis: Queue names
    const xAxisLabels = queues.length > 0 ? queues : ["No Data"];
    const data = ticket_counts.length > 0 ? ticket_counts : [0];

    // Single dataset showing ticket counts per queue
    const datasets = [
      {
        label: "Total Tickets",
        data: data,
        borderColor: "#6f42c1",
      },
    ];

    return {
      labels: xAxisLabels,
      datasets: datasets,
    };
  }, [summaryData]);

  // ── Shared Y-axis
  const maxVal = summaryData.ticket_counts?.length > 0
    ? Math.max(...summaryData.ticket_counts)
    : 0;

  const getStepSize = (max) =>
    max > 5000 ? 1000 :
    max > 1000 ? 500  :
    max > 500  ? 100  :
    max > 100  ? 25   :
    max > 10   ? 5    : 1;

  const sharedScales = {
    x: {
      ticks: { color: getAxisTextColor(), font: { size: 12 } },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      ticks: {
        color: getAxisTextColor(),
        font: { size: 12 },
        stepSize: getStepSize(maxVal),
        callback: (value) => (Math.floor(value) === value ? value : null),
      },
      suggestedMax: maxVal > 0 ? undefined : 5,
      grid: { display: false },
      border: { display: false },
    },
  };
  // ─────────────────────────────────────────────────────────────────────────

  const sharedTooltip = {
    enabled: true,
    mode: "index",
    backgroundColor: "#000000",
    titleColor: "#ffffff",
    bodyColor: "#ffffff",
    borderColor: "#000000",
    borderWidth: 0,
    padding: 10,
    displayColors: true,
    titleFont: { weight: "bold" },
    bodyFont: { size: 12 },
  };

  // The "Help Desk" bar is only ever present when this client has Freshservice ticket
  // data (see apply_helpdesk_bar in spog_dashboard/utils.py), so its presence alone is
  // enough to know it's safe to link to the Helpdesk analytics page.
  const isHelpdeskLabel = (label) => Boolean(onHelpdeskBarClick) && label === HELP_DESK_LABEL;

  const handleChartClick = (_event, elements) => {
    if (!elements.length) return;
    const label = chartData.labels?.[elements[0].index];
    if (isHelpdeskLabel(label)) onHelpdeskBarClick?.();
  };

  const handleChartHover = (event, elements) => {
    const label = elements.length ? chartData.labels?.[elements[0].index] : null;
    const target = event?.native?.target;
    if (target) target.style.cursor = isHelpdeskLabel(label) ? "pointer" : "default";
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="shadow-sm" style={{ borderRadius: 16 }}>
        <Card.Body>
          <div className="mb-4">
            <div
              className="d-flex align-items-start justify-content-between gap-2"
              style={{ rowGap: 4, flexWrap: "wrap" }}
            >
              <div className="flex-shrink-0">
                <h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      background: "rgba(245,158,11,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FaTasks size={16} color="#f59e0b" />
                  </div>
                  Tickets by Queue
                </h6>
                <small style={{ opacity: 0.7, color: "var(--color-text-light)", display: "block", marginTop: 2 }}>{getSubtitle()}</small>
              </div>
              <div
                className="d-flex align-items-end gap-2"
                style={{
                  justifyContent: "flex-start",
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                  width: "100%",
                  marginTop: 6,
                  overflowX: isMobile ? "auto" : "visible",
                }}
              >
              {/* Date Range Filter */}
              <div className="d-flex flex-column gap-1 position-relative" ref={datePickerRef} style={{ overflow: "visible" }}>
                <small style={{ fontSize: 14, color: "var(--color-text-light)", fontWeight: 600 }}>Date Range</small>
                <div style={{ position: "relative", overflow: "visible" }}>
                  <div
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 12px",
                      borderRadius: 10,
                      background: "var(--color-bg-card)",
                      border: "1px solid rgba(139, 92, 246, 0.3)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      minWidth: isMobile ? 170 : 170,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)";
                      e.currentTarget.style.boxShadow = "0 3px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.3)";
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)";
                    }}
                  >
                    <FaCalendarAlt size={11} color="#8b5cf6" />
                    <span style={{ 
                      color: "#8b5cf6", 
                      fontWeight: 600,
                      fontSize: 11,
                      flex: 1,
                      maxWidth: 130,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {formatDateForDisplay(dateRange.display.start)} - {formatDateForDisplay(dateRange.display.end)}
                    </span>
                    <div
                      onClick={clearDateRange}
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 5,
                        background: "rgba(239, 68, 68, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.background = "rgba(239, 68, 68, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.stopPropagation();
                        e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
                      }}
                    >
                      <FaTimes size={8} color="#ef4444" />
                    </div>
                    <FaChevronDown
                      size={10}
                      style={{
                        color: "#8b5cf6",
                        opacity: 0.7,
                        transition: "transform 0.2s ease",
                        transform: showDatePicker ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </div>
                  
                  {/* Date Range Picker Popup */}
                  <AnimatePresence>
                    {showDatePicker && (
                      <DateRangePicker
                        startDate={dateRange.api.start}
                        endDate={dateRange.api.end}
                        onRangeSelect={handleDateRangeSelect}
                        onClose={() => setShowDatePicker(false)}
                      />
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* CHART */}
          <div style={{ minHeight: "50dvh", position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: document.body.classList.contains('dark-mode') ? "rgba(30, 33, 57, 0.8)" : "rgba(255, 255, 255, 0.8)",
                  borderRadius: 16,
                  backdropFilter: "blur(4px)",
                }}
              >
                <div className="d-flex flex-column align-items-center gap-3">
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 40 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ height: [15, 40, 15] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                        style={{
                          width: 8,
                          background: "var(--color-primary)",
                          borderRadius: "4px 4px 0 0",
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ color: "var(--color-text)", fontWeight: 600, fontSize: 14 }}>Fetching Data...</span>
                </div>
              </div>
            )}

            {!loading && (
              <Bar
                data={{
                  ...chartData,
                  datasets: chartData.datasets.map((dataset) => ({
                    ...dataset,
                    backgroundColor: chartData.labels.map((label) =>
                      label === HELP_DESK_LABEL ? "#f59e0bCC" : dataset.borderColor + "CC"
                    ),
                    borderColor: chartData.labels.map((label) =>
                      label === HELP_DESK_LABEL ? "#f59e0b" : dataset.borderColor
                    ),
                    borderWidth: 2,
                    borderRadius: 6,
                    barPercentage: 0.7,
                    categoryPercentage: 0.8,
                  })),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: "index", intersect: false },
                  onClick: handleChartClick,
                  onHover: handleChartHover,
                  plugins: {
                    legend: { display: false },
                    tooltip: sharedTooltip,
                  },
                  scales: sharedScales,
                }}
              />
            )}
          </div>
        </Card.Body>
      </Card>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </motion.div>
  );
};

export default TicketQueueAreaChart;